import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import SimpleInput from 'dpl/form/components/SimpleInput';
import {
  FILTER_DISTANCE_IN_MILES,
  FILTER_POSTAL_CODE,
  PAGE,
  LOCATION_REDIRECT,
  SORT
} from 'dpl/constants/query_params';
import { pushParams, resetParams } from 'dpl/actions/query_params';
import { debounce } from 'dpl/shared/utils';
import { addToQuery, removeFromQuery } from 'dpl/util/queryString';
import {
  RESULTS_TYPES,
  SORT_NEAR_YOU
} from 'dpl/puppy_search_results/utils/constants';

import FilterDrawer from './FilterDrawer';

const DEFAULT_POSTAL_CODE = '';

export default function LocationFilter({ resultsType }) {
  const queryParams = useSelector(state => state.queryParams);
  const { preappliedFilters, rootPath, distanceOptions } = useSelector(
    ({ server }) => server
  );
  const defaultDistanceOption = distanceOptions.find(
    opt => opt.default === true
  );

  const [postalCode, setPostalCode] = useState(DEFAULT_POSTAL_CODE);

  const distanceInMiles = queryParams[FILTER_DISTANCE_IN_MILES];
  const [distanceOption, setDistanceOption] = useState(
    distanceOptions.find(({ value }) => value === distanceInMiles) ||
      defaultDistanceOption
  );

  const dispatch = useDispatch();

  function resetLocationFilters() {
    setDistanceOption(defaultDistanceOption);
    dispatch(resetParams(PAGE));
    dispatch(
      resetParams(FILTER_DISTANCE_IN_MILES, FILTER_POSTAL_CODE, { hash: true })
    );
  }
  const zipCodeRef = useRef();
  const { current: setLocationFilter } = useRef(
    debounce((postal, distance) => {
      const params = {
        [FILTER_POSTAL_CODE]: postal,
        [FILTER_DISTANCE_IN_MILES]: distance
      };

      if (preappliedFilters?.stateSlug) {
        const newSearchStr = removeFromQuery(PAGE, window.location.search);

        window.location.href = `${rootPath}/?${LOCATION_REDIRECT}=true&${newSearchStr}#${addToQuery(
          params,
          window.location.hash
        )}`;
      } else {
        dispatch(resetParams(PAGE));
        dispatch(pushParams(params, { hash: true }));
      }
    }, 500)
  );

  function handlePostalCodeChange(postal) {
    setPostalCode(postal);
    if (postal.length === 0) {
      resetLocationFilters();
      dispatch(resetParams(SORT, { hash: true }));
    }
    if (postal.length >= 5) {
      setLocationFilter(postal, distanceOption.value);

      if (resultsType === RESULTS_TYPES.BREEDERS) {
        dispatch(pushParams({ [SORT]: SORT_NEAR_YOU }, { hash: true }));
      }
    }
  }

  function onScroll(e) {
    e.preventDefault();
  }

  function removeOnScrollEvent() {
    if (zipCodeRef.current) {
      zipCodeRef.current.removeEventListener('wheel', onScroll);
    }
  }

  function preventDefaultOnScroll() {
    if (zipCodeRef.current) {
      zipCodeRef.current.addEventListener('wheel', onScroll);
    }
  }

  /*
    HACK: This makes sure if the location queryParams are updated outside of the filter itself,
    such as the clear button in the search results, the filter will match the queryParams value.
  */
  useEffect(() => {
    const updatedPostalCode =
      queryParams[FILTER_POSTAL_CODE] || DEFAULT_POSTAL_CODE;

    const updatedDistanceInMiles = queryParams[FILTER_DISTANCE_IN_MILES];

    if (postalCode && postalCode !== updatedPostalCode) {
      handlePostalCodeChange(updatedPostalCode);
    }

    if (
      updatedDistanceInMiles &&
      distanceOption.value !== updatedDistanceInMiles
    ) {
      const updatedDistanceOption =
        distanceOptions.find(({ value }) => value === updatedDistanceInMiles) ||
        defaultDistanceOption;
      setDistanceOption(updatedDistanceOption);
      setLocationFilter(updatedPostalCode, updatedDistanceOption.value);
    }
  }, [queryParams[FILTER_POSTAL_CODE], queryParams[FILTER_DISTANCE_IN_MILES]]);

  return (
    <FilterDrawer
      className="BreederSearchResultsLocationFilter"
      title="Location"
      onDrawerOpen={preventDefaultOnScroll}
      onDrawerClose={removeOnScrollEvent}
    >
      <p className="fw-medium f2 mb2">Zip code</p>
      <SimpleInput
        type="number"
        className="w-100 ba mb6 b--light-gray br3 pv2 ph3"
        name="postal-code"
        pattern="[0-9]*"
        value={postalCode}
        placeholder="Enter zip code"
        ref={zipCodeRef}
        onChange={e => handlePostalCodeChange(e.target.value)}
      />
    </FilterDrawer>
  );
}

LocationFilter.propTypes = {
  resultsType: PropTypes.oneOf(Object.values(RESULTS_TYPES))
};

LocationFilter.defaultProps = {
  resultsType: RESULTS_TYPES.PUPPIES
};
